"use client";

import { isServer, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import type React from "react";
import { Suspense } from "react";
import { AnalyticsTracker } from "@/components/analytics.tracker";
import { AppMainWidthProvider } from "@/components/app-main-width";
import { ThemeProvider } from "@/components/theme-provider";
import { ThemeSyncBridge } from "@/components/theme-sync-bridge";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";

const makeQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
      },
    },
  });

let browserQueryClient: QueryClient | undefined;

const getQueryClient = () => {
  if (isServer) {
    return makeQueryClient();
  }

  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }

  return browserQueryClient;
};

export const Providers = ({ children }: { children: React.ReactNode }) => {
  const queryClient = getQueryClient();

  return (
    <ThemeProvider>
      <ThemeSyncBridge />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Suspense fallback={null}>
            <AnalyticsTracker />
          </Suspense>
          <AppMainWidthProvider>
            <NuqsAdapter>
              <div className="app-root">
                {children}
                <Toaster />
              </div>
            </NuqsAdapter>
          </AppMainWidthProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};
